import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import {
  templateService,
  type ThemeCategory,
  type ThemeTemplate,
  type TemplateIcon,
  type TemplateUpsertInput,
} from '../services/themeService';
import '../styles/Pages.css';

type FilterCategory = string; // 'all' | ThemeCategory['id']

const ICONS: { value: TemplateIcon; label: string; symbol: string }[] = [
  { value: 'box',       label: 'Box (3D cube)',     symbol: '🟦' },
  { value: 'whiteboard', label: 'Whiteboard',        symbol: '📋' },
  { value: 'heart',     label: 'Heart (speech bubble)', symbol: '💬' },
];

interface DraftTemplate {
  id?: string;          // present = editing, absent = creating
  title: string;
  description: string;
  keywordsRaw: string;  // user-facing comma/newline-separated string
  category_id: string;
  icon_type: TemplateIcon;
  sort_order: number;
}

const emptyDraft = (defaultCategoryId: string): DraftTemplate => ({
  title: '',
  description: '',
  keywordsRaw: '',
  category_id: defaultCategoryId,
  icon_type: 'box',
  sort_order: 99,
});

/**
 * Convert a user's keyword input into a clean string[] for storage.
 * Real users paste things like:
 *   "slow, lag, fast\nload time,  timeout  "
 *   "  "                                 ← empty input
 *   "OKR,OKR,goal"                       ← duplicates
 * We need to: split on commas/newlines, trim, drop empties, dedupe
 * (case-insensitively — 'OKR' and 'okr' are the same keyword).
 *
 * Order: preserve the first appearance order; dedupe case-insensitively
 * but keep the original casing of the first occurrence.
 *
 * IMPORTANT: returning a list here is what gets passed to Supabase as
 * the `keywords` text[] column, so the order and uniqueness of what
 * you return is what end users will see later in the picker.
 */
function parseKeywords(input: string): string[] {
  // Split on commas or newlines, trim outer whitespace
  const raw = input.split(/[,\n]/).map((k) => k.trim());

  // Lowercase set for fast dedup lookup, array for preserved order + casing
  const seen = new Set<string>();
  const result: string[] = [];

  for (const keyword of raw) {
    if (!keyword) continue; // skip empty after trimming
    const lower = keyword.toLowerCase();
    if (seen.has(lower)) continue; // duplicate (case-insensitive) — skip

    seen.add(lower);
    result.push(keyword);
  }

  return result;
}

function draftToInput(d: DraftTemplate): TemplateUpsertInput {
  return {
    title: d.title.trim(),
    description: d.description.trim(),
    keywords: parseKeywords(d.keywordsRaw),
    category_id: d.category_id,
    icon_type: d.icon_type,
    sort_order: Number.isFinite(d.sort_order) ? d.sort_order : 99,
  };
}

function templateToDraft(t: ThemeTemplate, fallbackCategoryId: string): DraftTemplate {
  return {
    id: t.id,
    title: t.title,
    description: t.description,
    keywordsRaw: t.keywords.join(', '),
    category_id: t.category || fallbackCategoryId,
    icon_type: t.icon,
    sort_order: t.sortOrder,
  };
}

const Templates: React.FC = () => {
  const [categories, setCategories] = useState<ThemeCategory[]>([]);
  const [templates, setTemplates] = useState<ThemeTemplate[]>([]);
  const [filter, setFilter] = useState<FilterCategory>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftTemplate | null>(null);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cats, tpls] = await Promise.all([
        templateService.getCategories(),
        templateService.getTemplates(),
      ]);
      setCategories(cats);
      setTemplates(tpls);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.sortOrder - b.sortOrder),
    [categories],
  );

  const visibleTemplates = useMemo(() => {
    const list = filter === 'all' ? templates : templates.filter((t) => t.category === filter);
    return [...list].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [templates, filter]);

  const startCreate = () => {
    if (!categories.length) return;
    const firstReal = categories.find((c) => c.id !== 'all')?.id ?? categories[0].id;
    setDraft(emptyDraft(firstReal));
  };

  const startEdit = (t: ThemeTemplate) => {
    const firstReal = categories.find((c) => c.id !== 'all')?.id ?? categories[0]?.id ?? 'product';
    setDraft(templateToDraft(t, firstReal));
  };

  const cancelDraft = () => setDraft(null);

  const saveDraft = async () => {
    if (!draft) return;
    if (!draft.title.trim()) { setError('Title is required'); return; }
    setSaving(true); setError(null);
    try {
      const input = draftToInput(draft);
      if (draft.id) await templateService.updateTemplate(draft.id, input);
      else         await templateService.createTemplate(input);
      setDraft(null);
      await reload();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (t: ThemeTemplate) => {
    if (!window.confirm(`Delete template "${t.title}"? This cannot be undone.`)) return;
    setError(null);
    try {
      await templateService.deleteTemplate(t.id);
      await reload();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to delete template');
    }
  };

  return (
    <Layout>
      <div className="page-container">
        <div className="page-header">
          <h1>📋 Templates</h1>
          <button
            className="primary-button"
            onClick={startCreate}
            disabled={!categories.length}
          >
            + Create Template
          </button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        <div className="chip-row" role="tablist" aria-label="Template categories">
          {sortedCategories.map((c) => (
            <button
              key={c.id}
              role="tab"
              aria-selected={filter === c.id}
              className={`chip ${filter === c.id ? 'chip-active' : ''}`}
              onClick={() => setFilter(c.id)}
            >
              {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="empty-state"><p>Loading…</p></div>
        ) : visibleTemplates.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h2>No templates</h2>
            <p>Create reusable templates for common PM/BA documents</p>
          </div>
        ) : (
          <div className="card-grid">
            {visibleTemplates.map((t) => (
              <article key={t.id} className="template-card">
                <header className="template-card-header">
                  <span className="template-icon" aria-hidden>
                    {t.icon === 'box' ? '🟦' : t.icon === 'whiteboard' ? '📋' : '💬'}
                  </span>
                  <h3>{t.title}</h3>
                </header>
                <p className="template-desc">{t.description}</p>
                <p className="template-keywords">
                  <em>Keywords:</em> {t.keywords.join(', ')}
                </p>
                <div className="template-meta">
                  <span className="badge">{t.categoryLabel}</span>
                  <div className="template-actions">
                    <button className="secondary-button" onClick={() => startEdit(t)}>Edit</button>
                    <button className="danger-button" onClick={() => deleteTemplate(t)}>Delete</button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {draft && (
          <div className="modal-backdrop" onClick={cancelDraft}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2>{draft.id ? 'Edit template' : 'Create template'}</h2>
              <label className="field">
                <span>Title</span>
                <input
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="e.g. Accessibility"
                />
              </label>
              <label className="field">
                <span>Description</span>
                <textarea
                  rows={3}
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  placeholder="What kind of feedback should this template surface?"
                />
              </label>
              <label className="field">
                <span>Keywords (comma- or newline-separated)</span>
                <textarea
                  rows={3}
                  value={draft.keywordsRaw}
                  onChange={(e) => setDraft({ ...draft, keywordsRaw: e.target.value })}
                  placeholder="accessible, contrast, screen reader"
                />
              </label>
              <div className="field-row">
                <label className="field">
                  <span>Category</span>
                  <select
                    value={draft.category_id}
                    onChange={(e) => setDraft({ ...draft, category_id: e.target.value })}
                  >
                    {sortedCategories.filter((c) => c.id !== 'all').map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>Icon</span>
                  <select
                    value={draft.icon_type}
                    onChange={(e) => setDraft({ ...draft, icon_type: e.target.value as TemplateIcon })}
                  >
                    {ICONS.map((i) => (
                      <option key={i.value} value={i.value}>{i.symbol} {i.label}</option>
                    ))}
                  </select>
                </label>
                <label className="field field-narrow">
                  <span>Sort</span>
                  <input
                    type="number"
                    value={draft.sort_order}
                    onChange={(e) => setDraft({ ...draft, sort_order: Number(e.target.value) })}
                  />
                </label>
              </div>
              <div className="modal-actions">
                <button className="secondary-button" onClick={cancelDraft} disabled={saving}>Cancel</button>
                <button className="primary-button" onClick={saveDraft} disabled={saving}>
                  {saving ? 'Saving…' : draft.id ? 'Save changes' : 'Create template'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Templates;
